import React, {
  cloneElement,
  CSSProperties,
  Fragment,
  HTMLProps,
  isValidElement,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  arrow as arrowMiddleware,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  safePolygon,
  shift,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'

import { mergeRefs } from '../../utils/mergeRefs'

export type FloatingTrigger = 'hover' | 'click' | 'focus' | 'manual'

export interface FloatingRenderProps {
  // Styling/positioning
  floatingStyles: CSSProperties
  getFloatingProps: (userProps?: HTMLProps<HTMLElement>) => Record<string, unknown>
  placement: Placement
  middlewareData: ReturnType<typeof useFloating>['middlewareData']
  // Full floating-ui context (needed by components like FloatingArrow)
  context: ReturnType<typeof useFloating>['context']
  // Refs
  refs: ReturnType<typeof useFloating>['refs']
  // Arrow control
  arrowRef: MutableRefObject<HTMLDivElement | null>
  // Controls
  close: () => void
}

export interface FloatingContainerProps {
  // Called specifically when clicking outside. Return false to prevent default close.
  arrow?: boolean
  allowFlip?: boolean
  children?: ReactNode | ((ctx: FloatingRenderProps) => ReactNode)
  className?: string
  positioningTarget?: ReactNode // reference element
  defaultOpen?: boolean // uncontrolled initial state
  disabled?: boolean
  followCursor?: boolean
  hideOnClick?: boolean
  interactive?: boolean
  maxHeight?: number | string
  maxWidth?: number | string
  offset?: number | [number, number]
  onClickOut?: ((e: any) => void) | ((e: any) => boolean)
  onClickOutEvent?: 'click' | 'mousedown'
  onHide?: (() => void) | (() => boolean)
  onOpenChange?: (open: boolean) => void
  onShow?: (() => void) | (() => boolean)
  open?: boolean // controlled open state
  placement?: Placement
  portal?: boolean
  renderArrow?: boolean // whether to render a default arrow element when arrow middleware is enabled
  strategy?: 'absolute' | 'fixed'
  style?: CSSProperties
  trigger?: FloatingTrigger | FloatingTrigger[]
  width?: number | string
  zIndex?: number
}

export const FloatingContainer = ({
  className,
  children,
  positioningTarget,
  placement = 'bottom',
  strategy = 'absolute',
  arrow = false,
  allowFlip = true,
  interactive = false,
  disabled = false,
  followCursor = false,
  open: openProp,
  defaultOpen,
  onOpenChange,
  onShow,
  onHide,
  trigger = 'manual',
  onClickOutEvent,
  hideOnClick,
  offset: offsetProp,
  portal = true,
  zIndex = 9999,
  onClickOut,
}: FloatingContainerProps) => {
  const triggers = Array.isArray(trigger) ? trigger : [trigger]
  const hasHover = triggers.includes('hover')
  const hasClick = triggers.includes('click')
  const hasFocus = triggers.includes('focus')
  const isManual = triggers.includes('manual')

  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen)
  const open = openProp ?? uncontrolledOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) onShow?.()
      else {
        const result = onHide?.()
        if (result === false) return
      }
      if (openProp === undefined) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [onHide, onOpenChange, onShow, openProp],
  )

  const arrowRef = useRef<HTMLDivElement | null>(null)

  const middleware = useMemo(() => {
    const offsetOptions = Array.isArray(offsetProp)
      ? { mainAxis: offsetProp[1], crossAxis: offsetProp[0] }
      : offsetProp
    const middlewares = [
      offset(offsetOptions),
      ...(allowFlip ? [flip()] : []),
      shift({ padding: 8 }),
    ]
    if (arrow) middlewares.push(arrowMiddleware({ element: arrowRef }))
    return middlewares
  }, [allowFlip, arrow, offsetProp])

  const {
    refs,
    floatingStyles,
    context,
    placement: currentPlacement,
    middlewareData,
  } = useFloating({
    placement,
    strategy,
    open,
    onOpenChange: setOpen,
    middleware,
    whileElementsMounted: autoUpdate,
  })

  // Follow cursor support
  const clientPoint = useClientPoint(context, { enabled: followCursor })

  const hover = useHover(context, {
    enabled: !disabled && hasHover && !isManual,
    move: followCursor || undefined,
    restMs: 50,
    handleClose: interactive ? safePolygon() : undefined,
  })
  const click = useClick(context, {
    enabled: !disabled && hasClick && !isManual,
    toggle: true,
  })
  const focus = useFocus(context, {
    enabled: !disabled && (hasFocus || hasHover) && !isManual,
  })

  const clickOutTimer = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      clearInterval(clickOutTimer.current)
    }
  }, [])

  const dismiss = useDismiss(context, {
    referencePress: hideOnClick ?? (hasHover && !hasClick),
    outsidePress: e => {
      if (!open) return false
      if (onClickOut) {
        clickOutTimer.current = setTimeout(() => {
          onClickOut(e)
        }, 10) // timeout is here to execute onClickOutside after potential component unmounting by parent
        return false
      }
      return true
    },
    escapeKey: true,
    outsidePressEvent: onClickOutEvent || 'click',
  })
  const role = useRole(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
    clientPoint,
  ])

  const renderTrigger = (): ReactNode | null => {
    const triggerNode = positioningTarget
    if (!triggerNode) return <div ref={refs.setReference} {...getReferenceProps()} />

    const spanWrapped = () => (
      <span ref={refs.setReference} {...getReferenceProps({ style: { minWidth: 0 } })}>
        {triggerNode}
      </span>
    )

    if (
      typeof triggerNode === 'string' ||
      typeof triggerNode === 'number' ||
      typeof triggerNode === 'boolean' ||
      Array.isArray(triggerNode)
    ) {
      return spanWrapped()
    } else if (isValidElement(triggerNode)) {
      // for functional components not wrapped with React.forwardRef we wrap with span to make it work
      if (typeof triggerNode.type === 'function') {
        return spanWrapped()
      }

      const triggerNodeProps = getReferenceProps(triggerNode.props || {})
      // @ts-ignore
      const mergedRef = mergeRefs(triggerNode.ref, refs.setReference)

      // @ts-ignore
      return cloneElement(triggerNode, { ...triggerNodeProps, ref: mergedRef })
    }
  }

  const close = () => setOpen(false)

  const renderFloatingNode = () => {
    const ctx: FloatingRenderProps = {
      floatingStyles: { ...(floatingStyles as CSSProperties), zIndex },
      getFloatingProps,
      placement: currentPlacement,
      middlewareData,
      context,
      refs,
      arrowRef,
      close,
    }

    const floatingContent =
      typeof children === 'function'
        ? (children as (c: FloatingRenderProps) => ReactNode)(ctx)
        : children

    return (
      <div ref={refs.setFloating} {...getFloatingProps({ className, style: ctx.floatingStyles })}>
        {floatingContent}
      </div>
    )
  }

  const Body = portal ? FloatingPortal : Fragment

  return (
    <>
      {renderTrigger()}
      {open && !disabled ? <Body>{renderFloatingNode()}</Body> : null}
    </>
  )
}

export type { Placement }
